/**
 * Sign-in inside the Media Manager's frame: the Media Manager's own.
 *
 * The host hands over the access token its web app holds for its API
 * (`embed.ts`); this app's API accepts it for the Media Manager's groups only
 * (`core/rbac.py`). No MSAL, no popup and no account of the app's own: signing
 * in and out is the Media Manager's, whose sign-in the person already has.
 *
 * `getToken` keeps the token until it is five minutes from expiring and then
 * asks the host again, which renews it, so a long session needs no timer.
 * `reauthenticate` and `login` ask for a new one.
 */

import { type AccountInfo } from '@azure/msal-browser'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { sessionFromToken, type HostBridge, type HostSession } from '../embed'
import { AuthContext } from './authContextValue'

/** A token is asked for again this long before it expires. */
const RENEW_BEFORE_MS = 5 * 60 * 1000

function accountOf(session: HostSession): AccountInfo {
    return {
        homeAccountId: session.username,
        environment: 'login.microsoftonline.com',
        tenantId: '',
        username: session.username,
        localAccountId: session.username,
        name: session.name,
    }
}

export const HostAuthProvider: React.FC<{ children: React.ReactNode; bridge: HostBridge }> = ({ children, bridge }) => {
    const [session, setSession] = useState<HostSession | null>(null)
    const [loading, setLoading] = useState(true)
    const latest = useRef<HostSession | null>(null)

    const take = useCallback((token: string | null) => {
        const next = token ? sessionFromToken(token) : null
        latest.current = next
        setSession(next)
        return next
    }, [])

    const fetchSession = useCallback(
        async (fresh: boolean) => take(await bridge.requestToken(fresh)),
        [bridge, take],
    )

    useEffect(() => {
        let current = true
        void bridge.requestToken(false).then((token) => {
            if (!current) return
            take(token)
            setLoading(false)
        })
        return () => {
            current = false
        }
    }, [bridge, take])

    const getToken = useCallback(async () => {
        const known = latest.current
        if (known && known.expiresAt - Date.now() > RENEW_BEFORE_MS) return known.token
        return (await fetchSession(false))?.token ?? null
    }, [fetchSession])

    const reauthenticate = useCallback(async () => (await fetchSession(true)) !== null, [fetchSession])

    const login = useCallback(() => {
        setLoading(true)
        void fetchSession(true).finally(() => setLoading(false))
    }, [fetchSession])

    const logout = useCallback(() => {}, [])

    const value = useMemo(
        () => ({
            isAuthenticated: session !== null,
            user: session ? accountOf(session) : null,
            login,
            logout,
            reauthenticate,
            getToken,
            loading,
            authEnabled: true,
            hostSignIn: true,
        }),
        [session, login, logout, reauthenticate, getToken, loading],
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
